/**
 * Sidebar — Persistent app-level navigation shell with collapsible menu groups.
 *
 * Sidebar Component Guidelines
 * - Use Sidebar for the primary navigation structure of an application. It provides
 *   header, content, footer sections and supports collapsed (icon-only) and expanded
 *   states automatically.
 * - Wrap your app layout in `<SidebarProvider>` to enable sidebar state management
 *   (open/collapsed) and keyboard shortcut toggling.
 * - Use `SidebarGroup` + `SidebarGroupLabel` to organize navigation items into
 *   logical sections. Use `SidebarMenuSub` for nested sub-navigation.
 * - Use `SidebarMenuAction` for secondary actions on menu items (e.g. a "more"
 *   button or quick-action icon).
 * - On mobile, Sidebar automatically renders as a Sheet overlay instead of a
 *   persistent panel — no extra handling needed.
 * @see https://ui.shadcn.com/docs/components/sidebar shadcn/ui Sidebar documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@replohq/lattice/components/sheet/Sheet";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replohq/lattice/components/tooltip/Tooltip";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import { useRequiredContext } from "replo-utils/react/context";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3.5rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  return useRequiredContext(SidebarContext);
}

interface SidebarProviderProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarProvider<ClassName extends string>({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  UNSAFE_className,
  style,
  children,
  ...props
}: SidebarProviderProps<ClassName>) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen]);

  // Adds a keyboard shortcut to toggle the sidebar.
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        const target = event.target;
        if (target instanceof HTMLElement) {
          const isEditableElement =
            target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable;
          if (isEditableElement) {
            return;
          }
        }
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        style={
          // Legacy cast - may or may not be necessary
          // eslint-disable-next-line replo/no-as-cast
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "group/sidebar-wrapper has-data-[variant=inset]:bg-sheet flex min-h-svh w-full",
          className,
          UNSAFE_className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

interface SidebarProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function Sidebar<ClassName extends string>({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  UNSAFE_className,
  children,
  dir,
  ...props
}: SidebarProps<ClassName>) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sheet text-default flex h-full w-(--sidebar-width) flex-col",
          className,
          UNSAFE_className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          dir={dir}
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="bg-sheet text-default w-(--sidebar-width) p-0 [&>button]:hidden rounded-r-[10px]"
          style={
            // Legacy cast - may or may not be necessary
            // eslint-disable-next-line replo/no-as-cast
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="group peer text-default hidden md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "transition-[width] duration-200 ease-linear relative w-(--sidebar-width) bg-transparent",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(6)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        )}
      />
      <div
        data-slot="sidebar-container"
        data-side={side}
        className={cn(
          "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-3 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(6))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className,
          UNSAFE_className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sheet group-data-[variant=floating]:border group-data-[variant=floating]:border-border group-data-[variant=floating]:rounded-[10px] group-data-[variant=floating]:shadow-xs group-data-[variant=floating]:border-0.5 flex size-full flex-col"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

interface SidebarTriggerProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<typeof Button>, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarTrigger<ClassName extends string>({
  className,
  UNSAFE_className,
  onClick,
  ...props
}: SidebarTriggerProps<ClassName>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="tertiary"
      size="sm"
      icon
      UNSAFE_className={cn(className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

interface SidebarRailProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"button">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarRail<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarRailProps<ClassName>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "hover:after:bg-border absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sheet group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface SidebarInsetProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"main">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarInset<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarInsetProps<ClassName>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 relative flex min-w-0 w-full flex-1 flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      UNSAFE_className={cn("bg-background h-8 w-full shadow-none", className)}
      {...props}
    />
  );
}

interface SidebarSectionProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarHeader<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarSectionProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "gap-2 group-data-[collapsible=icon]:gap-3 p-3 flex flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarSectionProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("gap-2 p-3 flex flex-col", className, UNSAFE_className)}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      className={cn("w-auto bg-border", className as string)}
      {...props}
    />
  );
}

function SidebarContent<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarSectionProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "no-scrollbar gap-2 flex min-h-0 flex-1 flex-col overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarSectionProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "p-3 relative flex w-full min-w-0 flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  UNSAFE_className,
  render,
  ...props
}: useRender.ComponentProps<"div"> &
  React.ComponentProps<"div"> & {
    UNSAFE_className?: string;
  }) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(
          "text-muted ring-ring h-8 rounded-md px-2 text-xs font-medium transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 flex shrink-0 items-center outline-hidden [&>svg]:shrink-0",
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-group-label",
      sidebar: "group-label",
    },
  });
}

function SidebarGroupAction({
  className,
  UNSAFE_className,
  render,
  ...props
}: useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> & {
    UNSAFE_className?: string;
  }) {
  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(
          "text-default ring-ring hover:bg-menu-item-hover hover:text-default absolute top-3.5 right-3 w-5 rounded-md p-0 focus-visible:ring-2 [&>svg]:size-4 flex aspect-square items-center justify-center outline-hidden transition-transform [&>svg]:shrink-0 after:absolute after:-inset-2 md:after:hidden group-data-[collapsible=icon]:hidden",
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-group-action",
      sidebar: "group-action",
    },
  });
}

function SidebarGroupContent<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarSectionProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("text-sm w-full", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface SidebarListProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"ul">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarMenu<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarListProps<ClassName>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        "gap-2 flex w-full min-w-0 flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface SidebarListItemProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"li">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarMenuItem<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarListItemProps<ClassName>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative", className, UNSAFE_className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  "ring-ring hover:bg-sheet active:bg-menu-item-selected data-active:bg-menu-item-selected data-open:hover:bg-menu-item-selected rounded-sm group-data-[collapsible=icon]:rounded-md text-left text-sm transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! focus-visible:ring-2 data-active:font-medium peer/menu-button flex w-full items-center overflow-hidden outline-hidden group/menu-button disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-menu-item-hover hover:text-default",
        outline:
          "bg-background hover:bg-menu-item-hover hover:text-default shadow-[0_0_0_1px_hsl(var(--color-border))] hover:shadow-[0_0_0_1px_hsl(var(--color-component-border))]",
      },
      size: {
        default:
          "h-8 px-2 gap-1.5 py-1.5 text-sm group-data-[collapsible=icon]:p-2!",
        sm: "h-7 px-2 py-1.5 gap-1.5 text-xs group-data-[collapsible=icon]:p-2!",
        lg: "h-12 p-1.5 gap-2 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type SidebarMenuButtonProps = useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> & {
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
    UNSAFE_className?: string;
  } & VariantProps<typeof sidebarMenuButtonVariants>;

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonProps
>(function SidebarMenuButton(
  {
    render,
    isActive = false,
    variant = "default",
    size = "default",
    tooltip,
    className,
    UNSAFE_className,
    ...props
  },
  ref,
) {
  const { isMobile, state } = useSidebar();
  const comp = useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(
          sidebarMenuButtonVariants({ variant, size }),
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    ref,
    state: {
      slot: "sidebar-menu-button",
      sidebar: "menu-button",
      size,
      active: isActive,
    },
  });

  if (!tooltip) {
    return comp;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <TooltipRoot>
      <TooltipTrigger render={comp} />
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </TooltipRoot>
  );
});

function SidebarMenuAction({
  className,
  UNSAFE_className,
  render,
  showOnHover = false,
  ...props
}: useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> & {
    showOnHover?: boolean;
    UNSAFE_className?: string;
  }) {
  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(
          "text-default ring-ring hover:bg-menu-item-hover hover:text-default peer-hover/menu-button:text-default absolute top-1.5 right-1 aspect-square w-5 rounded-md p-0 peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 focus-visible:ring-2 [&>svg]:size-4 flex items-center justify-center outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 md:after:hidden [&>svg]:shrink-0",
          showOnHover &&
            "peer-data-active/menu-button:text-default group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 aria-expanded:opacity-100 md:opacity-0",
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-menu-action",
      sidebar: "menu-action",
    },
  });
}

interface SidebarMenuBadgeProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarMenuBadge<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarMenuBadgeProps<ClassName>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "text-default peer-hover/menu-button:text-default peer-data-active/menu-button:text-default pointer-events-none absolute right-1 h-5 min-w-5 rounded-md px-1 text-xs font-medium peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 flex items-center justify-center tabular-nums select-none group-data-[collapsible=icon]:hidden",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface SidebarMenuSkeletonProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  showIcon?: boolean;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function SidebarMenuSkeleton<ClassName extends string>({
  className,
  UNSAFE_className,
  showIcon = false,
  ...props
}: SidebarMenuSkeletonProps<ClassName>) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  });

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn(
        "h-8 gap-2 rounded-md px-2 flex items-center",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          // Legacy cast - may or may not be necessary
          // eslint-disable-next-line replo/no-as-cast
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarListProps<ClassName>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "border-l-border border-l-0.5 ml-3.5 translate-x-px gap-2 border-l pl-2.5 py-0.5 group-data-[collapsible=icon]:hidden flex min-w-0 flex-col mt-2",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: SidebarListItemProps<ClassName>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn(
        "group/menu-sub-item relative flex flex-col gap-2",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  render,
  size = "md",
  isActive = false,
  className,
  UNSAFE_className,
  ...props
}: useRender.ComponentProps<"a"> &
  React.ComponentProps<"a"> & {
    size?: "sm" | "md";
    isActive?: boolean;
    UNSAFE_className?: string;
  }) {
  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(
      {
        className: cn(
          "text-default ring-ring hover:bg-menu-item-hover hover:text-default active:bg-menu-item-hover active:text-default [&>svg]:text-default data-active:bg-menu-item-hover data-active:text-default h-7 gap-1.5 rounded-md px-2 py-1 focus-visible:ring-2 data-[size=md]:text-sm data-[size=sm]:text-xs [&>svg]:size-4 flex min-w-0 -translate-x-px items-center overflow-hidden outline-hidden group-data-[collapsible=icon]:hidden disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:shrink-0",
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-menu-sub-button",
      sidebar: "menu-sub-button",
      size,
      active: isActive,
    },
  });
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
